import type { DayKey } from "./types.js";

/* ============================================================
   Anúncios

   O Nhomi serve os PRÓPRIOS anúncios, e isso não é preferência: é a
   única saída. AdSense não entrega em WebView, AdMob é SDK nativo e não
   desenha dentro de uma página, e um TWA não tem WebView para registrar
   na ponte entre os dois. Empacotar fecha os três caminhos ao mesmo
   tempo.

   O lado bom de servir sozinho: nenhum script de terceiro no bundle,
   nenhum cookie de rastreamento, nenhum banner de consentimento — e a
   receita não passa por ninguém.

   Este módulo é só a REGRA de qual anúncio aparece onde. Ele não conhece
   Firestore: recebe a lista já normalizada e devolve o que vale hoje. É
   o que deixa a escolha ser a mesma no app e na prévia do painel.
   ============================================================ */

/**
 * Onde um anúncio pode aparecer.
 *
 * A lista é fechada de propósito. Posição nova é decisão de produto —
 * um campo de texto livre no painel viraria anúncio no meio do prato de
 * alguém sem ninguém ter escolhido isso.
 */
/* ---------- o tamanho da arte ----------

   Os números não são gosto: eles saem da caixa que o app desenha. A
   coluna do app tem 560px de largura com 16px de margem dos dois lados,
   então o anúncio ocupa 528px. A altura é o `max-height` de cada
   posição no CSS — 96px nas faixas, 190px no card.

   O RECOMENDADO é isso vezes 3, que é a densidade de tela de celular
   bom. Mandar menos deixa a arte borrada; mandar muito mais só pesa.

   A imagem é desenhada com `object-fit: cover`: ela preenche a largura
   e o que sobrar de altura é CORTADO PELO CENTRO. Ou seja, proporção
   errada não quebra o layout — ela come as bordas da arte, e é sempre
   o texto que fica perto da borda que some primeiro.

   Se mudar o CSS, mude aqui no mesmo commit. Uma spec que não bate com
   a caixa é pior que nenhuma: manda o anunciante refazer a arte errada
   com a nossa autoridade.  */

/** Teto do arquivo. Acima disso a faixa demora mais que o feed inteiro. */
export const PESO_MAXIMO_KB = 300;

export const POSICOES = [
  {
    id: "feed",
    nome: "Faixa no feed",
    onde: "Entre o placar e o primeiro dia de pratos, na tela do desafio.",
    formato: "Faixa larga e baixa. A arte é o anúncio inteiro.",
    largura: 1584,
    altura: 288,
    proporcao: "5,5:1",
    nota: "É a de maior alcance — todo mundo passa por ela ao abrir o desafio. "
      + "Também é a que mais empurra o conteúdo pra baixo.",
  },
  {
    id: "prato",
    nome: "Card no prato",
    onde: "Na tela do prato aberto, entre a nota e os comentários.",
    formato: "Card alto, com botão embaixo se você preencher o destino.",
    largura: 1584,
    altura: 570,
    proporcao: "2,8:1",
    nota: "É a de maior valor: quem abriu um prato está com fome e parado. "
      + "Por ser parecida com conteúdo, o rótulo PATROCINADO é obrigatório — "
      + "e o app o desenha sozinho.",
  },
  {
    id: "classificacao",
    nome: "Faixa na classificação",
    onde: "Abaixo do ranking, antes de 'todas as classificações'.",
    formato: "Faixa larga e baixa, do mesmo tamanho da do feed.",
    largura: 1584,
    altura: 288,
    proporcao: "5,5:1",
    nota: "Menos gente passa por aqui, mas quem passa está sem pressa.",
  },
] as const;

/** Onde o selo PATROCINADO cai, em fração da arte. Canto superior esquerdo. */
export const AREA_DO_SELO = "canto superior esquerdo";

/**
 * A proporção da caixa, pronta pra `aspect-ratio` do CSS.
 *
 * A CAIXA É PROPORÇÃO, NÃO ALTURA FIXA — e isso não é detalhe de
 * implementação, é o que faz a spec ser verdade.
 *
 * Com altura fixa, a caixa tem a largura da tela: 528×96 num celular
 * largo e 358×96 num estreito. São duas proporções diferentes, então
 * `object-fit: cover` corta a mesma arte de dois jeitos, e a medida que
 * o painel promete ao anunciante não vale em aparelho nenhum além de um.
 * Com `aspect-ratio`, o corte é o mesmo em toda tela — e é exatamente o
 * que a prévia mostra.
 *
 * O preço é a faixa ficar mais baixa em celular estreito. É o preço
 * certo: quem compra o espaço precisa saber o que aparece dele.
 *
 * Quem desenha usa ESTA função, no app e no painel. Repetir a conta nos
 * dois lugares é como os dois cortes divergiram na primeira vez.
 */
export function proporcaoDe(id: PosicaoId): string | null {
  const p = POSICOES.find((x) => x.id === id);
  return p ? `${p.largura} / ${p.altura}` : null;
}

export interface Aviso {
  posicao: PosicaoId;
  texto: string;
}

/**
 * Confere a arte contra as posições escolhidas.
 *
 * Devolve AVISO, não erro: a arte fora da medida ainda vai ao ar,
 * cortada. Recusar o envio obrigaria o anunciante a refazer por causa
 * de dez pixels, e quem cadastra costuma ser quem menos pode mexer no
 * arquivo. Dizer o que vai acontecer é mais útil que barrar.
 *
 * A tolerância é de 5% na proporção. Abaixo disso o corte é invisível.
 */
export function conferirArte(
  largura: number,
  altura: number,
  posicoes: readonly PosicaoId[],
): Aviso[] {
  if (!(largura > 0) || !(altura > 0)) return [];

  const avisos: Aviso[] = [];
  for (const id of posicoes) {
    const spec = POSICOES.find((p) => p.id === id);
    if (!spec) continue;

    const querida = spec.largura / spec.altura;
    const dela = largura / altura;
    const fora = Math.abs(dela - querida) / querida;

    if (fora > 0.05) {
      /* Dizer PARA QUE LADO o corte vai: "mais alta que o espaço"
         avisa que o topo e a base somem, e é isso que decide onde o
         anunciante põe o logo. */
      avisos.push({
        posicao: id,
        texto: dela < querida
          ? `A arte é mais alta que o espaço — o topo e a base vão ser cortados. `
            + `O ideal é ${spec.proporcao}.`
          : `A arte é mais larga que o espaço — as laterais vão ser cortadas. `
            + `O ideal é ${spec.proporcao}.`,
      });
      continue;
    }

    if (largura < spec.largura * 0.8) {
      avisos.push({
        posicao: id,
        texto: `A proporção está certa, mas ${largura}px de largura vai aparecer `
          + `borrado em tela de celular. O recomendado é ${spec.largura}px.`,
      });
    }
  }
  return avisos;
}

export type PosicaoId = typeof POSICOES[number]["id"];

const IDS = POSICOES.map((p) => p.id) as readonly string[];

export const posicaoValida = (v: unknown): v is PosicaoId =>
  typeof v === "string" && IDS.includes(v);

export interface Anuncio {
  id: string;
  /* TÍTULO E TEXTO SÃO INTERNOS. Eles não chegam ao app: servem pra
     achar o anúncio na lista do painel e pra escrever o que foi
     combinado com o anunciante.

     A peça que a pessoa vê é a IMAGEM, inteira — é o formato que todo
     anunciante já produz, e escrever o recado em cima da arte dele
     nunca combinava tipografia com a nossa. */
  /** Só o painel enxerga. Serve pra identificar o anúncio. */
  titulo: string;
  /** Só o painel enxerga. Anotação livre sobre a campanha. */
  texto: string;
  /** Caminho no Storage. Sem ele o anúncio NÃO vai ao ar: a imagem é a peça. */
  imagemPath: string | null;
  /** O que um leitor de tela anuncia no lugar da imagem. */
  alt: string;
  /** Para onde o toque leva. Vazio faz o anúncio não ser clicável. */
  url: string;
  /** O texto do botão. Só o card do prato usa. */
  cta: string;
  posicoes: PosicaoId[];
  ativo: boolean;
  /** Janela de veiculação. Vazio dos dois lados = sem limite. */
  de: DayKey | "";
  ate: DayKey | "";
}

/** Está no ar hoje? */
export function noAr(anuncio: Anuncio, hoje: DayKey): boolean {
  if (!anuncio.ativo) return false;
  if (anuncio.de && hoje < anuncio.de) return false;
  if (anuncio.ate && hoje > anuncio.ate) return false;
  return true;
}

/**
 * Qual anúncio mostrar numa posição.
 *
 * Gira pelo DIA, não por sorteio: com sorteio, cada rolagem do feed
 * troca o anúncio na cara da pessoa, e nenhum anunciante é visto duas
 * vezes pela mesma pessoa. Girar por dia dá a cada um o seu dia e
 * mantém a tela quieta.
 *
 * `null` quando não há nada — a tela não desenha caixa vazia.
 */
export function anuncioDoDia(
  anuncios: readonly Anuncio[], posicao: PosicaoId, hoje: DayKey,
): Anuncio | null {
  const valem = anuncios
    .filter((a) => noAr(a, hoje) && a.posicoes.includes(posicao))
    .sort((a, b) => a.id.localeCompare(b.id));   // ordem estável
  if (!valem.length) return null;

  // O dia vira um número; o resto escolhe. Sem estado, sem sorteio, e
  // igual em todo aparelho — o que torna possível conferir o que foi
  // veiculado depois.
  const semana = Number(hoje.replaceAll("-", ""));
  return valem[semana % valem.length] ?? null;
}

/* ============================================================
   Medir anúncio

   Sem número, o que se diz a um anunciante é "confia". Com número, se
   diz quanto custou cada clique.

   O desenho tem uma decisão no meio: como contar ÚNICO sem guardar a
   lista de quem viu. Guardar uids num array cresce sem parar e vira dado
   pessoal de graça; contar por sessão erra pra menos. A saída é um
   documento por pessoa por dia por anúncio — `vistos/{uid}` —, e o
   NASCIMENTO dele é o que soma um único. A pessoa escreve só o próprio
   documento; quem soma é o servidor.

   Custo: uma escrita pequena por pessoa por anúncio por dia, mais uma do
   gatilho. Não é uma escrita por impressão.
   ============================================================ */

/** O que o servidor soma por anúncio e por dia. */
export interface DiaDeAnuncio {
  dia: DayKey;
  impressoes: number;
  /** Pessoas distintas que viram no dia. */
  unicas: number;
  cliques: number;
  /** Pessoas distintas que clicaram no dia. */
  cliquesUnicos: number;
}

export interface ResumoDeAnuncio {
  impressoes: number;
  unicas: number;
  cliques: number;
  cliquesUnicos: number;
  /** Cliques por cem impressões. `null` sem impressão — 0/0 não é zero. */
  ctr: number | null;
  /** Impressões por pessoa. Diz se o anúncio está martelando as mesmas. */
  frequencia: number | null;
}

const soma = (dias: readonly DiaDeAnuncio[], campo: keyof DiaDeAnuncio): number =>
  dias.reduce((s, d) => s + (Number(d[campo]) || 0), 0);

/**
 * Junta os dias num resumo.
 *
 * `unicas` é somado por dia, e isso é proposital: a mesma pessoa em dois
 * dias conta duas vezes. "Únicos no período" exigiria guardar quem viu
 * ao longo do período, que é justamente o que este desenho evita — e
 * "alcance diário somado" é a métrica que se usa pra vender de qualquer
 * jeito. Quem lê precisa saber disso, então a tela chama de "por dia".
 */
export function resumirAnuncio(dias: readonly DiaDeAnuncio[]): ResumoDeAnuncio {
  const impressoes = soma(dias, "impressoes");
  const unicas = soma(dias, "unicas");
  const cliques = soma(dias, "cliques");
  return {
    impressoes,
    unicas,
    cliques,
    cliquesUnicos: soma(dias, "cliquesUnicos"),
    ctr: impressoes ? (cliques / impressoes) * 100 : null,
    frequencia: unicas ? impressoes / unicas : null,
  };
}
